import { observer } from 'mobx-react-lite'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateIconDecoration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { Form, FormRow } from '@/components/Form'
import { NumericInputContainer } from '@/components/inputs/NumericInputContainer'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import { TabItem } from '@/components/TabBar'
import {
  Inspector,
  InspectorContent,
  InspectorHeader,
  InspectorSection,
} from '@/features/legends/components/Inspector'
import { ErrorTabItem, TabLabel } from '@/features/legends/components/TabLabel'
import { FiltersBuilderContainer } from '@/features/legends/filters/components/FiltersBuilder/FiltersBuilderContainer'
import type { RoutingEditorViewModel } from '@/features/legends/routing/RoutingEditorViewModel'
import type {
  RoutingPlanEditorViewModel,
  RoutingPlanTab,
} from '@/features/legends/routing/RoutingPlanEditorViewModel'
import type { RoutingPlansBuilderViewModel } from '@/features/legends/routing/RoutingPlansBuilderViewModel'

const planTabs: { tab: RoutingPlanTab; title: string }[] = [
  { tab: 'General', title: 'General' },
  { tab: 'Requirements', title: 'Requirements' },
]

export interface RoutingInspectorContainerProps {
  vm: RoutingEditorViewModel
  testId?: string
}

/**
 * Edits the qualifying requirements or the selected routing plan.
 */
export const RoutingInspectorContainer = observer(
  function RoutingInspectorContainer({
    vm,
    testId,
  }: RoutingInspectorContainerProps) {
    const planVm = vm.plans.selectedPlanVm
    if (vm.isEditingRequirements) {
      return <RequirementsInspectorContainer vm={vm} testId={testId} />
    }
    if (planVm) {
      return (
        <PlanInspectorContainer
          key={planVm.cid}
          vm={vm.plans}
          planVm={planVm}
          testId={testId}
        />
      )
    }
    return (
      <Inspector testId={testId}>
        <EmptyState size="sm" grow>
          <EmptyStateIconDecoration icon="route" />
          <EmptyStateTitle>Nothing selected</EmptyStateTitle>
          <EmptyStateDescription>
            Select the qualifying requirements or a routing plan to edit it.
          </EmptyStateDescription>
        </EmptyState>
      </Inspector>
    )
  },
)

const RequirementsInspectorContainer = observer(
  function RequirementsInspectorContainer({
    vm,
    testId,
  }: RoutingInspectorContainerProps) {
    return (
      <Inspector testId={testId}>
        <InspectorHeader
          tabs={
            <TabItem active tooltip={vm.validation.getError('requirements')}>
              <TabLabel error={vm.validation.getError('requirements')}>
                Requirements
              </TabLabel>
            </TabItem>
          }
        />
        <InspectorContent>
          <InspectorSection>
            <FiltersBuilderContainer
              vm={vm.requirements}
              emptyText="No qualifying requirements configured; everyone will match."
              childLabel="AND"
              testId={testId && `${testId}-requirements`}
            />
          </InspectorSection>
        </InspectorContent>
      </Inspector>
    )
  },
)

interface PlanInspectorContainerProps {
  vm: RoutingPlansBuilderViewModel
  planVm: RoutingPlanEditorViewModel
  testId?: string
}

const PlanInspectorContainer = observer(function PlanInspectorContainer({
  vm,
  planVm,
  testId,
}: PlanInspectorContainerProps) {
  return (
    <Inspector testId={testId}>
      <InspectorHeader
        tabs={planTabs.map(({ tab, title }) => (
          <ErrorTabItem
            key={tab}
            title={title}
            error={planVm.getTabError(tab)}
            isActive={vm.currentTab === tab}
            badgeCount={
              tab === 'Requirements' ? planVm.requirements.length : undefined
            }
            onPress={() => vm.setTab(tab)}
            testId={testId && `${testId}-${tab.toLowerCase()}-tab`}
          />
        ))}
      />
      <InspectorContent>
        <InspectorSection>
          {vm.currentTab === 'General' ? (
            <Form>
              <FormRow>
                <TextInputContainer
                  model={planVm}
                  field="label"
                  label="Label"
                  help="Useful for previewing."
                  error={planVm.validation.getError('label')}
                  isDisabled={planVm.isEditingDisabled}
                  data-test={testId && `${testId}-label`}
                />
              </FormRow>
              <FormRow>
                <TextInputContainer
                  model={planVm}
                  field="description"
                  label="Description"
                  help="Useful for providing some context as to what the plan is for."
                  error={planVm.validation.getError('description')}
                  isDisabled={planVm.isEditingDisabled}
                  data-test={testId && `${testId}-description`}
                />
              </FormRow>
              <FormRow>
                <NumericInputContainer
                  model={planVm}
                  field="duration"
                  label="Duration (in minutes)"
                  help="How long should this plan be active for? When elapsed, the next plan (if any) is activated."
                  error={planVm.validation.getError('duration')}
                  isDisabled={planVm.isEditingDisabled}
                  testId={testId && `${testId}-duration`}
                />
              </FormRow>
            </Form>
          ) : (
            <FiltersBuilderContainer
              vm={planVm.requirements}
              emptyText="No plan requirements configured yet."
              childLabel="AND"
              testId={testId && `${testId}-plan-requirements`}
            />
          )}
        </InspectorSection>
      </InspectorContent>
    </Inspector>
  )
})
